import React, { useCallback, useEffect } from 'react';
import styled from "styled-components"
import {
  ArrowDownTrayIcon as InstallIcon,
  // VideoCameraIcon as AnimationEditIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon as RestartPlayIcon,
  ArrowUturnLeftIcon,
  CameraIcon as ExportIcon,
  BackwardIcon as PrevSnapshotIcon,
  ForwardIcon as NextSnapshotIcon,
  PlayIcon as PlayerIcon,
  PlayPauseIcon,
  NumberedListIcon,
} from "@heroicons/react/24/outline"

import { ReactComponent as CapNumberingIcon } from "../assets/cap-numbering.svg"
import { isHandball } from "../sportConfig"
import { ReactComponent as AnimationEditIcon } from "../assets/play-new-edit.svg"
import { ReactComponent as RotateIcon } from "../assets/rotate.svg"

import { ArrowLongRightIcon, MinusIcon } from "@heroicons/react/24/solid"
import { ControlButton } from "./ControlButton"
import { Panel } from "./Panel"
import { DrawingMode } from "../DrawingCanvas"
import { useStore } from '@nanostores/react';
import { $activeSnapshotIndex, $tactic } from '../store/tactic';
import { darkBlueBackground, green, yellow } from './colors';

// Player-numbering toolbar icon: handball uses jersey numbers (a numeric-list
// icon), waterpolo uses cap numbers (the cap icon).
const NumberingIcon = isHandball ? NumberedListIcon : CapNumberingIcon

export enum ToolbarControl {
  DRAWING,
  PLAYING,
  EDITING,
  NUMBERING,
}

const TacticBoardMainControls = styled(Panel)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  margin-left: 0.4rem;
  color: #000;
`

const ToolbarSubMenuContainer = styled(Panel)<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${(props) => (props.$isVisible ? "auto" : "0")};
  overflow: hidden;
  transition-property: height;
  transition-duration: 0.5s;
  background: none;
  border-bottom: ${(props) => (props.$isVisible ? "1px solid rgba(0, 0, 0, 0.2)" : "none")};
  box-shadow: ${(props) => (props.$isVisible ? "0px 0px 15px rgba(0, 0, 0, 0.2)" : "none")};
`

export const Toolbar: React.FC<{
  activeControl?: ToolbarControl
  isSubscribed: boolean
  isPlayable: boolean
  colorMode: "light" | "dark"
  isInstallable: boolean
  onControlChange: (control?: ToolbarControl) => void
  onColorModeChange: (colorMode: "light" | "dark") => void
  onCaptureScreen: () => void
  drawingMode: DrawingMode
  onDrawingModeChange: (mode: DrawingMode) => void
  onCleanDrawings: (shouldClean: boolean) => void
  onUndoLastDrawing: (shouldUndo: boolean) => void
  onRotatePlay: () => void
  onInstall: () => void
  //here come the recording props
  snapshotPosition?: string
  isPlaying: boolean
  onNextSnapshot: () => void
  onPrevSnapshot: () => void
  onPlayPausePlay: () => void
  isReplayable?: boolean
  onResetPlay: () => void
  isEditing: boolean
  onEditPlay: () => void
  onUpgrade?: () => void
}> = ({
  activeControl,
  onControlChange,
  isSubscribed,
  isPlayable,
  isInstallable,
  onCaptureScreen,
  drawingMode,
  onDrawingModeChange,
  onCleanDrawings,
  onUndoLastDrawing,
  onRotatePlay,
  onInstall,
  //here come the recording props
  onNextSnapshot,
  onPrevSnapshot,
  snapshotPosition,
  isPlaying,
  onPlayPausePlay,
  isReplayable = false,
  onResetPlay,
  onUpgrade
}) => {
  useEffect(() => {
    if (activeControl !== ToolbarControl.DRAWING) {
      onDrawingModeChange(DrawingMode.NONE)
    }
  }, [activeControl, onDrawingModeChange])


  const handleToggleToolbar = useCallback((control: ToolbarControl) => {
      // if (activeControl !== undefined && activeControl === control) {
      //   return
      // }
      onControlChange(activeControl !== control ? control : undefined)
    },
    [activeControl, onControlChange])

  const activeSnapshotIndex = useStore($activeSnapshotIndex)
  const tactic = useStore($tactic)

  return (
    <>
      <TacticBoardMainControls>
        <>
          <ControlButton
            icon={<AnimationEditIcon />}
            onClick={() => {
              if (!isSubscribed) {
                onUpgrade?.()
                return
              }

              handleToggleToolbar(ToolbarControl.EDITING)
            }}
            isActive={activeControl === ToolbarControl.EDITING}
            onlyPro={true}
            isPro={isSubscribed}
            // isDisabled={
            //   activeControl !== undefined &&
            //   activeControl !== ToolbarControl.EDITING
            // }
            inverse
          />
          {/* <ToolbarSubMenuContainer
              $isVisible={activeControl === ToolbarControl.EDITING}
            /> */}
        </>
        {isPlayable && (
          <>
            <ControlButton
              icon={<PlayerIcon />}
              onClick={() => {
                if (!isSubscribed) {
                  onUpgrade?.()
                  return
                }
                handleToggleToolbar(ToolbarControl.PLAYING)
              }}
              onlyPro={true}
              isPro={isSubscribed}
              isActive={activeControl === ToolbarControl.PLAYING}
              // isDisabled={
              //   activeControl !== undefined &&
              //   activeControl !== ToolbarControl.PLAYING
              // }
              inverse
            />
            <ToolbarSubMenuContainer
              $isVisible={activeControl === ToolbarControl.PLAYING}
            >
              <ControlButton
                icon={<NextSnapshotIcon />}
                onClick={onNextSnapshot}
              />
              <div style={{ position: "relative"}}>
                {isReplayable ? (
                  <ControlButton
                    icon={<RestartPlayIcon />}
                    onClick={onResetPlay}
                  />
                ) : (
                  <ControlButton
                    icon={<PlayPauseIcon />}
                    onClick={onPlayPausePlay}
                  />
                )}
                {/*Snapshot position progress bar*/}
                {tactic && activeSnapshotIndex !== undefined && activeSnapshotIndex !== null && tactic.snapshots.length > 0 && (
                  <div style={{ position: "absolute", bottom: "0px", width: "100%" }}>
                    <div style={{ width: `${(activeSnapshotIndex / (tactic.snapshots.length - 1)) * 100}%`, height: "2px", backgroundColor: green, borderRadius: "2px" }} />
                  </div>
                )}
              </div>
              <ControlButton
                icon={<PrevSnapshotIcon />}
                onClick={onPrevSnapshot}
              />
              {snapshotPosition && <ControlButton icon={snapshotPosition} />}
            </ToolbarSubMenuContainer>
          </>
        )}
        <ControlButton
          icon={<RotateIcon />}
          onClick={onRotatePlay}
          isActive={false}
          isDisabled={false}
          inverse
        />
        <ControlButton
          icon={<PencilIcon />}
          onClick={() => handleToggleToolbar(ToolbarControl.DRAWING)}
          isActive={activeControl === ToolbarControl.DRAWING}
          // isDisabled={
          //   activeControl !== undefined &&
          //   activeControl !== ToolbarControl.DRAWING
          // }
          inverse
        />
        <ToolbarSubMenuContainer
          $isVisible={activeControl === ToolbarControl.DRAWING}
        >
          <ControlButton
            icon={<ArrowLongRightIcon />}
            onClick={() => {
              onDrawingModeChange(
                drawingMode !== DrawingMode.ARROW
                  ? DrawingMode.ARROW
                  : DrawingMode.NONE
              )
            }}
            isActive={drawingMode === DrawingMode.ARROW}
          />
          <ControlButton
            icon={<MinusIcon />}
            onClick={() => {
              onDrawingModeChange(
                drawingMode !== DrawingMode.LINE
                  ? DrawingMode.LINE
                  : DrawingMode.NONE
              )
            }}
            isActive={drawingMode === DrawingMode.LINE}
          />
          <ControlButton
            icon={<TrashIcon />}
            onClick={() => onCleanDrawings(true)}
          />
          <ControlButton
            icon={<ArrowUturnLeftIcon />}
            onClick={() => onUndoLastDrawing(true)}
          />
        </ToolbarSubMenuContainer>
        {/* <ControlButton
          icon={<ColorSwitchIcon />}
          onClick={() => {
            onColorModeChange(colorMode === "dark" ? "light" : "dark")
          }}
          isDisabled={activeControl !== undefined}
          isActive={colorMode === "light"}
        /> */}
        <ControlButton
          icon={<ExportIcon />}
          onlyPro={true}
          isPro={isSubscribed}
          onClick={async () => {
            if (!isSubscribed) {
              onUpgrade?.()
              return
            }
            await onCaptureScreen()
          }}
          isDisabled={activeControl !== undefined}
          inverse
        />
        <ControlButton
          icon={<NumberingIcon />}
          onClick={() => {
            if (!isSubscribed) {
              onUpgrade?.()
              return
            }
            handleToggleToolbar(ToolbarControl.NUMBERING)
          }}
          onlyPro={true}
          isPro={isSubscribed}
          // isDisabled={
          //   activeControl !== undefined &&
          //   activeControl !== ToolbarControl.NUMBERING
          // }
          isActive={activeControl === ToolbarControl.NUMBERING}
          inverse
        />
        {isInstallable && (
          <ControlButton
            icon={<InstallIcon />}
            onClick={onInstall}
            inverse
            isDisabled={activeControl !== undefined}
          />
        )}
      </TacticBoardMainControls>
    </>
  )
}
